/**
 * platform-mark.tsx — dấu nhận biết nền tảng của một kênh.
 *
 * Trước đây mỗi màn tự vẽ một kiểu: danh sách hội thoại có logo đè góc avatar,
 * màn huấn luyện AI thì chỉ có chữ trong Badge, ô chọn tài khoản thì không có
 * gì. Cùng một khái niệm mà ba cách hiển thị — người dùng phải học lại ở từng
 * màn. Gom về một chỗ để sửa một lần là đổi khắp nơi.
 *
 * Hai dạng dùng cho hai chỗ khác nhau:
 *  - `PlatformBadge` — chấm nhỏ đè GÓC DƯỚI PHẢI ảnh đại diện. Dùng khi đã có
 *    avatar và chỉ cần biết "kênh nào", không chiếm thêm chỗ.
 *  - `PlatformTag`  — nhãn có icon + chữ. Dùng trong danh sách/bộ lọc, nơi cần
 *    đọc được tên nền tảng chứ không phải đoán theo màu.
 */
import { Bot, Globe } from 'lucide-react'
import { BRAND_LOGOS, type BrandKey } from '@/lib/brand-logos'
import { PLATFORM_LABELS, platformLabelOf } from '@/lib/platform-labels'
import { cn } from '@/lib/utils'

interface PlatformMeta {
  /** Tên đầy đủ — dùng cho tooltip và nhãn. */
  label: string
  /** Ký tự viết tắt cho chấm nhỏ; không có thì dùng icon. */
  short?: string
  /** Màu nền của chấm nhỏ. */
  bg: string
  icon?: typeof Globe
  /** Logo thật của nền tảng — có thì dùng thay chữ/icon (xem lib/brand-logos.ts). */
  logo?: BrandKey
  /** Kênh bot dùng chung logo với kênh gốc (Zalo Bot) → thêm huy hiệu robot nhỏ. */
  bot?: boolean
  /** Logo đã có nền màu riêng (tròn) → phủ kín chấm, không đặt trên nền trắng. */
  fill?: boolean
}

/** Khớp với `Platform` trong `shared/constants.ts` của backend; nhãn lấy từ lib/platform-labels. */
const L = PLATFORM_LABELS
const PLATFORM_META: Record<number, PlatformMeta> = {
  // Zalo OA: dấu riêng chữ "OA" (tròn xanh) — không dùng logo Zalo để khỏi lẫn với Zalo cá nhân.
  1: { label: L[1], short: 'OA', bg: '#0068ff', logo: 'zaloOa', fill: true },
  2: { label: L[2], short: 'Z', bg: '#0068ff', logo: 'zalo' },
  // Zalo Bot dùng lại logo Zalo (không tự vẽ logo hãng); nhãn + huy hiệu bot phân biệt với OA / cá nhân.
  3: { label: L[3], short: 'B', bg: '#0A8FDC', logo: 'zalo', bot: true },
  10: { label: L[10], short: 'M', bg: 'linear-gradient(45deg,#0099FF,#A033FF,#FF5280)', logo: 'messenger' },
  11: { label: L[11], bg: 'linear-gradient(45deg,#F58529,#DD2A7B,#8134AF)', logo: 'instagram' },
  12: { label: L[12], bg: '#229ED9', logo: 'telegram' },
  13: { label: L[13], bg: '#229ED9', logo: 'telegram' },
  20: { label: L[20], bg: '#475569', icon: Globe },
  // Tin qua Pancake: chấm hiện logo mạng gốc của khách, nhãn vẫn ghi "Pancake (…)".
  30: { label: L[30], short: 'P', bg: '#1d4ed8', logo: 'messenger' },
  31: { label: L[31], short: 'P', bg: '#be185d', logo: 'instagram' },
  32: { label: L[32], short: 'P', bg: '#0f172a', logo: 'tiktok' },
  39: { label: L[39], short: 'P', bg: '#334155', logo: 'pancake' },
  40: { label: L[40], bg: '#0f172a', logo: 'tiktok' },
  41: { label: L[41], short: 'S', bg: '#EE4D2D', logo: 'shopee' },
}

export function platformLabel(platform?: number | null): string {
  return platformLabelOf(platform)
}

/** Huy hiệu robot nhỏ đè góc logo — phân biệt Zalo Bot với Zalo OA / cá nhân (cùng logo Zalo). */
function BotDot({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('absolute -right-1 -top-1 flex items-center justify-center rounded-full bg-[#0A8FDC] text-white ring-1 ring-white', className)}
    >
      <Bot className="h-[70%] w-[70%]" />
    </span>
  )
}

/**
 * Chấm nền tảng đè góc dưới phải ảnh đại diện.
 *
 * Phần tử cha PHẢI có `relative`, nếu không chấm sẽ neo nhầm chỗ.
 * `ring` cùng màu nền thẻ để chấm tách khỏi ảnh, không bị lẫn khi avatar tối màu.
 */
export function PlatformBadge({
  platform,
  className,
  title,
}: {
  platform?: number | null
  className?: string
  /** Chú thích khi rê chuột (mặc định tên nền tảng) — vd "Zalo OA · Phường Sơn Tây". */
  title?: string
}) {
  if (platform == null) return null
  const meta = PLATFORM_META[platform]
  if (!meta) return null
  const Icon = meta.icon

  return (
    <span
      className={cn(
        'absolute -bottom-0.5 -right-0.5 flex h-4 w-4 select-none items-center justify-center',
        'rounded-full ring-2 ring-card text-[7px] font-extrabold leading-none text-white',
        className,
      )}
      // Logo thương hiệu nằm trên nền trắng (cả dark mode) để giữ đúng màu hãng; logo có nền riêng (fill) phủ kín chấm.
      style={{ background: meta.logo ? (meta.fill ? meta.bg : '#fff') : meta.bg }}
      title={title ?? meta.label}
      aria-label={title ?? meta.label}
    >
      {meta.logo && meta.fill ? (
        <img src={BRAND_LOGOS[meta.logo].src} alt="" draggable={false} className="h-full w-full rounded-full object-contain" />
      ) : meta.logo ? (
        <span className="relative flex h-2.5 w-2.5">
          <img src={BRAND_LOGOS[meta.logo].src} alt="" draggable={false} className="h-2.5 w-2.5 object-contain" />
          {meta.bot && <BotDot className="h-[7px] w-[7px]" />}
        </span>
      ) : (
        (meta.short ?? (Icon ? <Icon className="h-2.5 w-2.5" /> : null))
      )}
    </span>
  )
}

/**
 * Nhãn nền tảng có icon.
 *
 * Bo góc NHỎ (`rounded`, 4px) chứ không bo tròn: nhãn bo tròn trông như nút bấm
 * được, mà đây chỉ là thông tin. Cũng để phân biệt với Badge trạng thái
 * ("Đã kết nối") vốn bo tròn và thật sự là trạng thái động.
 */
export function PlatformTag({
  platform,
  className,
}: {
  platform?: number | null
  className?: string
}) {
  if (platform == null) return null
  const meta = PLATFORM_META[platform]
  const Icon = meta?.icon
  const label = platformLabelOf(platform)

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded border px-1.5 py-0.5',
        'text-[10px] font-medium leading-none text-muted-foreground',
        className,
      )}
      title={label}
    >
      {meta?.logo ? (
        <span className="relative flex shrink-0">
          <img
            src={BRAND_LOGOS[meta.logo].src} alt="" aria-hidden draggable={false}
            className={cn('h-3 w-3 shrink-0 object-contain', meta.fill ? 'rounded-full' : 'rounded-[2px] bg-white p-px')}
          />
          {meta.bot && <BotDot className="h-2 w-2" />}
        </span>
      ) : (
        <span
          className="flex h-3 w-3 shrink-0 items-center justify-center rounded-[2px] text-[6px] font-extrabold leading-none text-white"
          style={{ background: meta?.bg ?? '#64748b' }}
          aria-hidden
        >
          {meta?.short ?? (Icon ? <Icon className="h-2 w-2" /> : null)}
        </span>
      )}
      {label}
    </span>
  )
}
